/**
 * ChatPreviewSection - Interactive WhatsApp chat preview
 * Shows NARA in action with animated messages across 3 scenarios
 */
import React, { useState, useEffect, useRef } from 'react';
import { Typography, Box, Container } from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import HistoryIcon from '@mui/icons-material/History';

import { COLORS, STYLES, TYPOGRAPHY, SPACING, TRANSITIONS } from './landingTheme';
import { ScrollReveal } from './animations';

// Chat scenarios
const SCENARIOS = [
  {
    id: 'diagnostico',
    label: 'Diagnóstico',
    icon: MedicalServicesIcon,
    color: '#8b5cf6',
    messages: [
      { from: 'user', text: 'Hola, tengo una erupción roja en el brazo desde hace 3 días', time: '10:15' },
      { from: 'bot', text: 'Hola María 💚 Lamento que estés pasando por eso. Voy a analizar tus síntomas:', time: '10:15' },
      { from: 'bot', text: '📋 Posibles causas:\n1. Dermatitis de contacto (más común)\n2. Reacción alérgica\n3. Eczema\n\n🩺 ¿La erupción pica o duele? ¿Estuviste en contacto con algún producto nuevo?', time: '10:15' },
      { from: 'user', text: 'Sí, pica mucho. Empecé a usar un jabón nuevo', time: '10:16' },
      { from: 'bot', text: '✅ Eso confirma la dermatitis de contacto. Te recomiendo:\n\n🌿 Suspender el jabón nuevo\n🌿 Aplicar crema con avena coloidal\n🌿 Si no mejora en 48h, agenda una consulta presencial\n\n¿Te agendo una cita por precaución? 🤗', time: '10:16' },
    ],
  },
  {
    id: 'citas',
    label: 'Citas',
    icon: EventAvailableIcon,
    color: '#0ea5e9',
    messages: [
      { from: 'user', text: 'Necesito una cita con el doctor para el viernes', time: '14:20' },
      { from: 'bot', text: '¡Claro! 📅 Estos son los horarios disponibles para el viernes 14 de febrero:', time: '14:20' },
      { from: 'bot', text: '✅ 9:00 AM\n✅ 10:30 AM\n✅ 12:00 PM\n✅ 3:30 PM\n\n¿Cuál te queda mejor?', time: '14:20' },
      { from: 'user', text: 'A las 10:30 por favor', time: '14:21' },
      { from: 'bot', text: '¡Listo! Tu cita ha sido agendada ✅\n\n📋 Resumen:\n• Dr. García - Medicina General\n• Viernes 14 de febrero\n• 10:30 AM\n\nTe enviaré un recordatorio el día anterior 🔔', time: '14:21' },
    ],
  },
  {
    id: 'memoria',
    label: 'Memoria',
    icon: HistoryIcon,
    color: '#10b981',
    messages: [
      { from: 'user', text: '¿Qué me recetaron la última vez que vine?', time: '09:30' },
      { from: 'bot', text: 'Revisando tu historial... 🔍', time: '09:30' },
      { from: 'bot', text: '📋 En tu última consulta (15 de enero) te diagnosticaron faringitis aguda. El tratamiento fue:\n\n💊 Amoxicilina 500mg c/8h por 7 días\n💊 Ibuprofeno 400mg si hay dolor\n🌿 Reposo e hidratación\n\n¿Sigues con alguna molestia? 💚', time: '09:31' },
      { from: 'user', text: 'No, ya me siento mejor. Pero quería saber si era alérgica a algo', time: '09:31' },
      { from: 'bot', text: 'Según tu expediente, tienes registrada alergia a la penicilina ⚠️\n\nPor eso en tu última visita se recetó amoxicilina con precaución, ya que aunque es derivado de penicilina, el Dr. García evaluó que era seguro en tu caso.\n\n¿Algo más en lo que te pueda ayudar? 🤗', time: '09:32' },
    ],
  },
];

// Single chat bubble
const ChatBubble = ({ message, visible, delay }) => {
  const isUser = message.from === 'user';

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isUser ? 'flex-end' : 'flex-start',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0) scale(1)' : 'translateY(12px) scale(0.95)',
        transition: `all 0.4s cubic-bezier(0.16, 1, 0.3, 1) ${delay}s`,
      }}
    >
      <Box
        sx={{
          background: isUser ? '#dcf8c6' : '#fff',
          px: 1.5,
          py: 0.8,
          borderRadius: isUser ? '10px 10px 3px 10px' : '10px 10px 10px 3px',
          maxWidth: '82%',
          boxShadow: '0 1px 1px rgba(0,0,0,0.08)',
        }}
      >
        <Typography
          sx={{
            fontSize: { xs: 10.5, md: 11.5 },
            color: '#1a1a1a',
            lineHeight: 1.5,
            whiteSpace: 'pre-line',
          }}
        >
          {message.text}
        </Typography>
        <Typography sx={{ fontSize: 8, color: '#999', textAlign: 'right', mt: 0.3 }}>
          {message.time} {isUser && '✓✓'}
        </Typography>
      </Box>
    </Box>
  );
};

// Phone with animated chat
const AnimatedPhone = ({ scenario }) => {
  const [visibleCount, setVisibleCount] = useState(0);
  const chatRef = useRef(null);

  // Reset and animate messages when scenario changes
  useEffect(() => {
    setVisibleCount(0);
    const timers = [];

    scenario.messages.forEach((_, idx) => {
      const timer = setTimeout(() => {
        setVisibleCount(prev => prev + 1);
      }, 400 + idx * 700);
      timers.push(timer);
    });

    return () => timers.forEach(clearTimeout);
  }, [scenario.id]);

  // Auto-scroll chat as messages appear
  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTo({
        top: chatRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [visibleCount]);

  return (
    <Box
      sx={{
        width: { xs: 280, sm: 300, md: 320 },
        mx: 'auto',
        position: 'relative',
        // Glow
        '&::before': {
          content: '""',
          position: 'absolute',
          top: '10%', left: '-12%', right: '-12%', bottom: '5%',
          background: `radial-gradient(ellipse, ${scenario.color}15 0%, transparent 70%)`,
          borderRadius: '50%',
          filter: 'blur(40px)',
          pointerEvents: 'none',
        },
      }}
    >
      {/* Phone frame */}
      <Box
        sx={{
          borderRadius: '28px',
          border: `2px solid ${COLORS.gray200}`,
          background: '#f8fafc',
          overflow: 'hidden',
          boxShadow: `0 25px 60px rgba(0,0,0,0.12), 0 8px 24px rgba(0,0,0,0.08)`,
          position: 'relative',
        }}
      >
        {/* Status bar */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2.5, py: 0.75, background: '#075e54' }}>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {[1, 2, 3].map(i => (
              <Box key={i} sx={{ width: 4, height: 4, borderRadius: '50%', background: 'rgba(255,255,255,0.5)' }} />
            ))}
          </Box>
          <Typography sx={{ fontSize: 10, color: 'rgba(255,255,255,0.8)', fontWeight: 600 }}>12:30</Typography>
          <Box sx={{ display: 'flex', gap: 0.3, alignItems: 'center' }}>
            <Box sx={{ width: 12, height: 6, borderRadius: 0.5, border: '1px solid rgba(255,255,255,0.5)' }} />
          </Box>
        </Box>

        {/* WhatsApp header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, px: 2, py: 1.2, background: '#075e54' }}>
          <Box sx={{ width: 32, height: 32, borderRadius: '50%', background: 'linear-gradient(135deg, #25D366, #128C7E)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <SmartToyIcon sx={{ fontSize: 18, color: '#fff' }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#fff' }}>Dr. García - NARA</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 5, height: 5, borderRadius: '50%', background: '#25D366' }} />
              <Typography sx={{ fontSize: 9, color: 'rgba(255,255,255,0.7)' }}>en línea</Typography>
            </Box>
          </Box>
        </Box>

        {/* Chat area */}
        <Box
          ref={chatRef}
          sx={{
            p: 1.5,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.8,
            background: '#ece5dd',
            height: { xs: 340, md: 380 },
            overflowY: 'auto',
            // Hide scrollbar
            '&::-webkit-scrollbar': { display: 'none' },
            scrollbarWidth: 'none',
          }}
        >
          {scenario.messages.map((msg, idx) => (
            <ChatBubble
              key={`${scenario.id}-${idx}`}
              message={msg}
              visible={idx < visibleCount}
              delay={0}
            />
          ))}

          {/* Typing indicator */}
          {visibleCount < scenario.messages.length && visibleCount > 0 && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: scenario.messages[visibleCount]?.from === 'user' ? 'flex-end' : 'flex-start',
              }}
            >
              <Box
                sx={{
                  background: scenario.messages[visibleCount]?.from === 'user' ? '#dcf8c6' : '#fff',
                  px: 1.5,
                  py: 1,
                  borderRadius: '10px',
                  display: 'flex',
                  gap: 0.4,
                  boxShadow: '0 1px 1px rgba(0,0,0,0.08)',
                }}
              >
                {[0, 1, 2].map(i => (
                  <Box
                    key={i}
                    sx={{
                      width: 5,
                      height: 5,
                      borderRadius: '50%',
                      background: '#999',
                      animation: `typingDot 1.2s ease-in-out ${i * 0.2}s infinite`,
                      '@keyframes typingDot': {
                        '0%, 100%': { opacity: 0.3, transform: 'scale(0.8)' },
                        '50%': { opacity: 1, transform: 'scale(1)' },
                      },
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}
        </Box>

        {/* Input bar */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, background: '#f0f0f0' }}>
          <Box sx={{ flex: 1, py: 0.7, px: 1.5, borderRadius: 3, background: '#fff', border: '1px solid #e0e0e0' }}>
            <Typography sx={{ fontSize: 10, color: '#999' }}>Escribe un mensaje...</Typography>
          </Box>
          <Box sx={{ width: 28, height: 28, borderRadius: '50%', background: '#25D366', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <WhatsAppIcon sx={{ fontSize: 16, color: '#fff' }} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// Scenario tab
const ScenarioTab = ({ scenario, isActive, onClick }) => {
  const IconComponent = scenario.icon;

  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 2,
        py: 1.2,
        borderRadius: 2.5,
        cursor: 'pointer',
        transition: TRANSITIONS.normal,
        background: isActive ? `${scenario.color}10` : 'transparent',
        border: `1.5px solid ${isActive ? `${scenario.color}40` : COLORS.gray200}`,
        '&:hover': {
          background: `${scenario.color}08`,
          borderColor: `${scenario.color}30`,
        },
      }}
    >
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 2,
          background: isActive
            ? `linear-gradient(135deg, ${scenario.color}, ${scenario.color}cc)`
            : `${scenario.color}12`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: TRANSITIONS.normal,
        }}
      >
        <IconComponent
          sx={{
            fontSize: 17,
            color: isActive ? '#fff' : scenario.color,
            transition: TRANSITIONS.normal,
          }}
        />
      </Box>
      <Typography
        sx={{
          fontSize: '0.85rem',
          fontWeight: isActive ? 700 : 600,
          color: isActive ? scenario.color : COLORS.textSecondary,
          transition: TRANSITIONS.fast,
        }}
      >
        {scenario.label}
      </Typography>
    </Box>
  );
};

export default function ChatPreviewSection() {
  const [activeScenario, setActiveScenario] = useState(0);

  return (
    <Box
      sx={{
        py: SPACING.sectionY,
        background: COLORS.gray50,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Decorative blobs */}
      <Box sx={{ position: 'absolute', top: '15%', left: '-5%', width: 350, height: 350, borderRadius: '50%', background: 'radial-gradient(circle, rgba(139, 92, 246, 0.04) 0%, transparent 70%)', pointerEvents: 'none' }} />
      <Box sx={{ position: 'absolute', bottom: '10%', right: '-5%', width: 300, height: 300, borderRadius: '50%', background: 'radial-gradient(circle, rgba(14, 165, 233, 0.04) 0%, transparent 70%)', pointerEvents: 'none' }} />

      <Container maxWidth="lg">
        {/* Section header */}
        <ScrollReveal>
          <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 7 } }}>
            <Typography
              sx={{
                color: COLORS.accent,
                fontSize: TYPOGRAPHY.tiny,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                mb: 1.5,
              }}
            >
              NARA en acción
            </Typography>
            <Typography
              variant="h2"
              sx={{ ...STYLES.sectionTitle }}
            >
              Mira cómo funciona en tiempo real
            </Typography>
            <Typography
              sx={{ ...STYLES.sectionSubtitle }}
            >
              Así es como NARA interactúa con tus pacientes por WhatsApp — diagnóstico, citas y memoria en una sola conversación
            </Typography>
          </Box>
        </ScrollReveal>

        {/* Two-column: tabs left, phone right */}
        <ScrollReveal delay={0.1}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: { xs: 4, md: 6 },
              alignItems: 'center',
            }}
          >
            {/* Left: scenario selector + description */}
            <Box>
              {/* Scenario tabs */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 4 }}>
                {SCENARIOS.map((scenario, idx) => (
                  <ScenarioTab
                    key={scenario.id}
                    scenario={scenario}
                    isActive={activeScenario === idx}
                    onClick={() => setActiveScenario(idx)}
                  />
                ))}
              </Box>

              {/* Active scenario description */}
              <Box
                sx={{
                  p: 3,
                  borderRadius: 3,
                  background: COLORS.white,
                  border: `1px solid ${COLORS.gray200}`,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                }}
              >
                {activeScenario === 0 && (
                  <>
                    <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, color: COLORS.textPrimary, mb: 1 }}>
                      Triage inteligente de síntomas
                    </Typography>
                    <Typography sx={{ fontSize: '0.88rem', color: COLORS.textSecondary, lineHeight: 1.7, mb: 1.5 }}>
                      NARA analiza los síntomas del paciente usando IA médica, ordena las posibles causas
                      de más benigna a más seria, y recomienda el siguiente paso adecuado.
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {['GPT-4o', 'Triage IA', 'Tono empático'].map(tag => (
                        <Box key={tag} sx={{ px: 1, py: 0.3, borderRadius: 1, background: '#8b5cf612', border: '1px solid #8b5cf620' }}>
                          <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#8b5cf6' }}>{tag}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </>
                )}
                {activeScenario === 1 && (
                  <>
                    <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, color: COLORS.textPrimary, mb: 1 }}>
                      Agendamiento automático
                    </Typography>
                    <Typography sx={{ fontSize: '0.88rem', color: COLORS.textSecondary, lineHeight: 1.7, mb: 1.5 }}>
                      El paciente solicita una cita y NARA consulta la disponibilidad real del doctor,
                      muestra los horarios libres y confirma la reserva — todo en segundos.
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {['Function Calling', 'Agenda real', 'Recordatorios'].map(tag => (
                        <Box key={tag} sx={{ px: 1, py: 0.3, borderRadius: 1, background: '#0ea5e912', border: '1px solid #0ea5e920' }}>
                          <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#0ea5e9' }}>{tag}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </>
                )}
                {activeScenario === 2 && (
                  <>
                    <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, color: COLORS.textPrimary, mb: 1 }}>
                      Memoria semántica del paciente
                    </Typography>
                    <Typography sx={{ fontSize: '0.88rem', color: COLORS.textSecondary, lineHeight: 1.7, mb: 1.5 }}>
                      NARA recuerda diagnósticos, medicamentos, alergias y preferencias de cada paciente
                      hasta por 365 días. Cada conversación enriquece el perfil del paciente.
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {['Embeddings', '365 días', 'Auto-extracción'].map(tag => (
                        <Box key={tag} sx={{ px: 1, py: 0.3, borderRadius: 1, background: '#10b98112', border: '1px solid #10b98120' }}>
                          <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#10b981' }}>{tag}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </>
                )}
              </Box>
            </Box>

            {/* Right: phone mockup */}
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <AnimatedPhone scenario={SCENARIOS[activeScenario]} />
            </Box>
          </Box>
        </ScrollReveal>
      </Container>
    </Box>
  );
}
